/**
 * `docsHubApi` — RTK Query slice for the Documents library (`/documents`):
 * living markdown plus versioned binary artifacts, a folder tree, an
 * attributed revision history, and a Postgres edit lease.
 *
 * Calls the proxy **directly** at `/api/v1/docs*` + `/api/v1/doc-folders*`
 * (the dashboards/notification-rules pattern — no BFF hop), because every
 * operation is plain JSON over the proxy's `docs` package. Export and
 * binary HTML preview are the exceptions: Markdown is a client-side blob
 * download; DOCX export goes through BFF `GET /api/ontology/docs/:id/export`
 * (`docs-export-routes.ts`) so the UI never imports
 * `@atbash-labs/markdown-authoring`; docx/xlsx/csv preview goes through
 * `GET /api/ontology/docs/:id/preview` (`docs-preview-routes.ts`) so the UI
 * never imports `@atbash-labs/ontology-doc-template-filler`. The explorer lists
 * every format (`?format=*`); the proxy still defaults `GET /docs` to
 * markdown so other callers stay markdown-only.
 *
 * Cache shape: `'DocFolder'` for the tree, `'Doc'` per doc id plus a `'LIST'`
 * sentinel, `'DocRevision'` per doc id for the history panel. A lease renew
 * (heartbeat) deliberately invalidates nothing — it fires every ~30s while an
 * editor is open and only moves `lock_expires_at`, so refetching the whole
 * list on each tick would be pure churn.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { getOntologyServiceBaseUrl } from '../../config'
import { PROXY_API_V1_URL, ontologyBaseQuery } from '../core'
import type {
  AcquireDocLockInput,
  AcquireDocLockResponse,
  CopyDocFolderInput,
  CopyDocFolderResponse,
  CopyDocInput,
  CreateDocCommentInput,
  CreateDocFolderInput,
  CreateDocInput,
  Doc,
  DocComment,
  DocFolder,
  DocPreview,
  DocRevision,
  DocRevisionPage,
  ListDocRevisionsArgs,
  MoveDocFolderInput,
  MoveDocFolderResponse,
  RenameDocFolderInput,
  RestoreDocRevisionInput,
  UpdateDocContentInput,
  UpdateDocInput,
  UpdateDocStatusInput
} from './types'

const DOCS_URL = `${PROXY_API_V1_URL}/docs`
const DOC_FOLDERS_URL = `${PROXY_API_V1_URL}/doc-folders`
const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

export const DOCS_HUB_TAG_TYPES = ['DocFolder', 'Doc', 'DocRevision', 'DocComment'] as const
export type DocsHubTagType = (typeof DOCS_HUB_TAG_TYPES)[number]

const docId = (id: string): string => encodeURIComponent(id)

export const docsHubApi = createApi({
  reducerPath: 'docsHubApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: DOCS_HUB_TAG_TYPES,
  endpoints: (builder) => ({
    // ── Folders ─────────────────────────────────────────────────────
    listDocFolders: builder.query<DocFolder[], void>({
      query: () => DOC_FOLDERS_URL,
      transformResponse: (response: { folders?: DocFolder[] }) => response.folders ?? [],
      providesTags: ['DocFolder']
    }),
    createDocFolder: builder.mutation<DocFolder, CreateDocFolderInput>({
      query: (body) => ({ url: DOC_FOLDERS_URL, method: 'POST', body }),
      invalidatesTags: ['DocFolder']
    }),
    renameDocFolder: builder.mutation<DocFolder, { id: string; input: RenameDocFolderInput }>({
      query: ({ id, input }) => ({
        url: `${DOC_FOLDERS_URL}/${docId(id)}`,
        method: 'PATCH',
        body: input
      }),
      invalidatesTags: ['DocFolder']
    }),
    deleteDocFolder: builder.mutation<void, string>({
      query: (id) => ({ url: `${DOC_FOLDERS_URL}/${docId(id)}`, method: 'DELETE' }),
      invalidatesTags: ['DocFolder', 'Doc']
    }),
    moveDocFolder: builder.mutation<MoveDocFolderResponse, MoveDocFolderInput>({
      query: (body) => ({ url: `${DOC_FOLDERS_URL}/move`, method: 'POST', body }),
      invalidatesTags: ['DocFolder', 'Doc']
    }),
    copyDocFolder: builder.mutation<CopyDocFolderResponse, CopyDocFolderInput>({
      query: (body) => ({ url: `${DOC_FOLDERS_URL}/copy`, method: 'POST', body }),
      invalidatesTags: ['DocFolder', 'Doc']
    }),
    deleteDocFolderByPath: builder.mutation<void, { path: string }>({
      query: ({ path }) => ({
        url: `${DOC_FOLDERS_URL}/delete`,
        method: 'POST',
        body: { path }
      }),
      invalidatesTags: ['DocFolder', 'Doc']
    }),

    // ── Docs ────────────────────────────────────────────────────────
    listDocs: builder.query<Doc[], void>({
      query: () => ({ url: DOCS_URL, params: { format: '*' } }),
      transformResponse: (response: { docs?: Doc[] }) => response.docs ?? [],
      providesTags: (result) => [
        ...(result ?? []).map((doc) => ({ type: 'Doc' as const, id: doc.id })),
        { type: 'Doc' as const, id: 'LIST' }
      ]
    }),
    getDoc: builder.query<Doc, string>({
      query: (id) => `${DOCS_URL}/${docId(id)}`,
      providesTags: (_result, _error, id) => [{ type: 'Doc', id }]
    }),
    // In-browser HTML preview (docx/xlsx/csv) of the current revision, or of
    // `revisionId`. PDFs are previewed natively from revision bytes, so the UI
    // never calls this for them.
    getDocPreview: builder.query<DocPreview, { id: string; revisionId?: string }>({
      query: ({ id, revisionId }) => ({
        url: `${ONTOLOGY_SERVICE_URL}/docs/${encodeURIComponent(id)}/preview`,
        ...(revisionId ? { params: { revision: revisionId } } : {})
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Doc', id }]
    }),
    createDoc: builder.mutation<Doc, CreateDocInput>({
      query: (body) => ({ url: DOCS_URL, method: 'POST', body }),
      invalidatesTags: [{ type: 'Doc', id: 'LIST' }, 'DocFolder']
    }),
    copyDoc: builder.mutation<Doc, { id: string; input: CopyDocInput }>({
      query: ({ id, input }) => ({
        url: `${DOCS_URL}/${docId(id)}/copy`,
        method: 'POST',
        body: input
      }),
      invalidatesTags: [{ type: 'Doc', id: 'LIST' }, 'DocFolder']
    }),
    updateDoc: builder.mutation<Doc, { id: string; input: UpdateDocInput }>({
      query: ({ id, input }) => ({
        url: `${DOCS_URL}/${docId(id)}`,
        method: 'PATCH',
        body: input
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Doc', id },
        { type: 'Doc', id: 'LIST' },
        'DocFolder'
      ]
    }),
    deleteDoc: builder.mutation<void, string>({
      query: (id) => ({ url: `${DOCS_URL}/${docId(id)}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Doc', id },
        { type: 'Doc', id: 'LIST' },
        'DocFolder'
      ]
    }),
    // Save a revision. Rejects with 409 `DOC_REVISION_CONFLICT` when the lock
    // token or `expected_revision_id` no longer matches; the editor reads the
    // excerpt out of `error.data.details` and offers a re-read.
    updateDocContent: builder.mutation<Doc, { id: string; input: UpdateDocContentInput }>({
      query: ({ id, input }) => ({
        url: `${DOCS_URL}/${docId(id)}/content`,
        method: 'PUT',
        body: input
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Doc', id },
        { type: 'Doc', id: 'LIST' },
        { type: 'DocRevision', id }
      ]
    }),

    // ── Edit lease ──────────────────────────────────────────────────
    // Acquire, or renew by passing the token back. Intentionally invalidates
    // nothing: the heartbeat runs every ~30s and only moves the expiry.
    acquireDocLock: builder.mutation<
      AcquireDocLockResponse,
      { id: string; input: AcquireDocLockInput }
    >({
      query: ({ id, input }) => ({
        url: `${DOCS_URL}/${docId(id)}:acquireLock`,
        method: 'POST',
        body: input
      })
    }),
    releaseDocLock: builder.mutation<Doc, { id: string; lock_token: string }>({
      query: ({ id, lock_token }) => ({
        url: `${DOCS_URL}/${docId(id)}:releaseLock`,
        method: 'POST',
        body: { lock_token }
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Doc', id },
        { type: 'Doc', id: 'LIST' }
      ]
    }),

    // ── Revisions ───────────────────────────────────────────────────
    listDocRevisions: builder.query<DocRevisionPage, ListDocRevisionsArgs>({
      query: ({ docId: id, page_size, page_token }) => ({
        url: `${DOCS_URL}/${docId(id)}/revisions`,
        params: { page_size, page_token }
      }),
      transformResponse: (response: {
        revisions?: DocRevisionPage['revisions']
        next_page_token?: string
      }) => ({
        revisions: response.revisions ?? [],
        next_page_token: response.next_page_token ?? ''
      }),
      providesTags: (_result, _error, { docId: id }) => [{ type: 'DocRevision', id }]
    }),
    getDocRevision: builder.query<DocRevision, { docId: string; revisionId: string }>({
      query: ({ docId: id, revisionId }) =>
        `${DOCS_URL}/${docId(id)}/revisions/${docId(revisionId)}`,
      providesTags: (_result, _error, { docId: id }) => [{ type: 'DocRevision', id }]
    }),
    // Copy-forward restore: appends a new revision holding the old content, so
    // history never rewinds. Same lock + revision guards as a save.
    restoreDocRevision: builder.mutation<
      Doc,
      { docId: string; revisionId: string; input: RestoreDocRevisionInput }
    >({
      query: ({ docId: id, revisionId, input }) => ({
        url: `${DOCS_URL}/${docId(id)}/revisions/${docId(revisionId)}:restore`,
        method: 'POST',
        body: input
      }),
      invalidatesTags: (_result, _error, { docId: id }) => [
        { type: 'Doc', id },
        { type: 'Doc', id: 'LIST' },
        { type: 'DocRevision', id }
      ]
    }),

    // ── Lookup ──────────────────────────────────────────────────────
    searchDocs: builder.query<Doc[], { q: string; page_size?: number }>({
      query: ({ q, page_size }) => ({ url: `${DOCS_URL}:search`, params: { q, page_size } }),
      transformResponse: (response: { docs?: Doc[] }) => response.docs ?? [],
      providesTags: [{ type: 'Doc', id: 'LIST' }]
    }),
    resolveDoc: builder.query<Doc, string>({
      query: (path) => ({ url: `${DOCS_URL}:resolve`, params: { path } }),
      providesTags: (result) => (result ? [{ type: 'Doc', id: result.id }] : [])
    }),

    // ── Review ──────────────────────────────────────────────────────
    updateDocStatus: builder.mutation<Doc, { id: string; input: UpdateDocStatusInput }>({
      query: ({ id, input }) => ({
        url: `${DOCS_URL}/${docId(id)}/status`,
        method: 'PATCH',
        body: input
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Doc', id },
        { type: 'Doc', id: 'LIST' }
      ]
    }),
    listDocComments: builder.query<DocComment[], string>({
      query: (id) => `${DOCS_URL}/${docId(id)}/comments`,
      transformResponse: (response: { comments?: DocComment[] }) => response.comments ?? [],
      providesTags: (_result, _error, id) => [{ type: 'DocComment', id }]
    }),
    createDocComment: builder.mutation<DocComment, { id: string; input: CreateDocCommentInput }>({
      query: ({ id, input }) => ({
        url: `${DOCS_URL}/${docId(id)}/comments`,
        method: 'POST',
        body: input
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'DocComment', id }]
    }),
    updateDocComment: builder.mutation<
      DocComment,
      { commentId: string; docId: string; body: string }
    >({
      query: ({ commentId, body }) => ({
        url: `${PROXY_API_V1_URL}/doc-comments/${encodeURIComponent(commentId)}`,
        method: 'PATCH',
        body: { body }
      }),
      invalidatesTags: (_result, _error, { docId: id }) => [{ type: 'DocComment', id }]
    }),
    deleteDocComment: builder.mutation<void, { commentId: string; docId: string }>({
      query: ({ commentId }) => ({
        url: `${PROXY_API_V1_URL}/doc-comments/${encodeURIComponent(commentId)}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_result, _error, { docId: id }) => [{ type: 'DocComment', id }]
    })
  })
})

export const {
  useListDocFoldersQuery,
  useCreateDocFolderMutation,
  useRenameDocFolderMutation,
  useDeleteDocFolderMutation,
  useMoveDocFolderMutation,
  useCopyDocFolderMutation,
  useDeleteDocFolderByPathMutation,
  useListDocsQuery,
  useGetDocQuery,
  useGetDocPreviewQuery,
  useCreateDocMutation,
  useCopyDocMutation,
  useUpdateDocMutation,
  useDeleteDocMutation,
  useUpdateDocContentMutation,
  useAcquireDocLockMutation,
  useReleaseDocLockMutation,
  useListDocRevisionsQuery,
  useGetDocRevisionQuery,
  useLazyGetDocRevisionQuery,
  useRestoreDocRevisionMutation,
  useSearchDocsQuery,
  useResolveDocQuery,
  useUpdateDocStatusMutation,
  useListDocCommentsQuery,
  useCreateDocCommentMutation,
  useUpdateDocCommentMutation,
  useDeleteDocCommentMutation
} = docsHubApi
